import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  Loader2,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  DIET_TYPES,
  STRENGTH_BASIS_UNITS,
  SUGAR_STATUSES,
  TARGET_GROUPS,
  formStyles,
} from "../../components/Inventory-Stocks & Orders/Admin/Products/catalogConstants";
import { InverntroyService } from "../../services/api/inventroy.service";
import {
  ClassificationType,
  EnrichedProduct,
  ProductCatalogService,
  ResolvedRef,
  ResolvedSalt,
} from "../../services/api/productCatalog.service";
import { CategoryMappingRow } from "./tabs/ProductCategoryMappingTab";
import { SaltRow } from "./tabs/ProductSaltDtlsTab";

/** The masters this panel can resolve, and create when they are missing. */
type MasterKey =
  | "primaryCategory"
  | "subCategory"
  | "productGroup"
  | "productFormGroup"
  | "productForm"
  | "domain"
  | "regulatoryDomain"
  | "marketer"
  | "categoryHierarchy";

type RefMap = Record<MasterKey, ResolvedRef>;

interface PlanRow {
  key: string;
  label: string;
  /** What the agent proposed, as it will read in the form. */
  preview: string;
  /** What the form holds today. Empty when the field is blank. */
  current: string;
  /** The form patch this row contributes when it is ticked. */
  patch: Record<string, any>;
  /** Set when a master has to be created before the row can be applied. */
  blockedReason?: string;
  /** True when the form already holds a value, so the row starts unticked. */
  overwrites: boolean;
}

interface PlanGroup {
  title: string;
  note?: string;
  rows: PlanRow[];
}

interface ProductEnrichPanelProps {
  /** Read-only: the panel decides what to apply, it never mutates the form. */
  formData: any;
  isEdit: boolean;
  marketerOptions: { id: string; marketerName: string }[];
  /** Called with the fields the reviewer accepted. */
  onApply: (patch: Record<string, any>) => void;
  /** A marketer created here is not in the form's dropdown yet. */
  onMarketersChanged?: () => void;
  onClose: () => void;
}

const isBlank = (value: any) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/**
 * The form's selects only accept members of their own list, so a value the
 * service left as free text is dropped rather than written into a select it
 * would render as blank.
 */
const canonical = <T extends string>(
  value: string | null,
  allowed: readonly T[]
): T | null => {
  if (!value) return null;
  const needle = value.trim().toLowerCase();
  return allowed.find((item) => item.toLowerCase() === needle) ?? null;
};

const truncate = (value: string, limit = 120) =>
  value.length > limit ? `${value.slice(0, limit)}...` : value;

const COMPOSITION_SOURCE_NOTES: Record<
  EnrichedProduct["compositionSource"],
  string
> = {
  CATALOGUE:
    "Copied from an existing product of the same name, so these salts are already verified.",
  MODEL_GROUNDED:
    "Written by the model after it was shown real compositions for this brand. Check it against the pack.",
  MODEL_UNGROUNDED:
    "No catalogue product matched this name, so the composition is the model's own guess. Verify every salt against the pack before saving.",
  NONE: "No composition was returned.",
};

/**
 * Fills a sparse product in from the AI enrichment agent.
 *
 * Two deliberate rules shape this screen. First, nothing is overwritten: a
 * suggestion for a field that already holds a value starts unticked and says so,
 * because the person who typed that value knew more than the model did. Second,
 * no suggestion can be applied until the master it points at actually exists -
 * the service returns every reference as either a real id or `isNew`, and the
 * rows for anything new are blocked until it has been created here, which is
 * what stops a half-resolved classification reaching the create call.
 */
export function ProductEnrichPanel({
  formData,
  isEdit,
  marketerOptions,
  onApply,
  onMarketersChanged,
  onClose,
}: ProductEnrichPanelProps) {
  const currentMarketer = marketerOptions.find(
    (option) => option.id === formData.marketerId
  );

  const [request, setRequest] = useState({
    productName: formData.name || "",
    brandName: formData.brandName || "",
    marketerName: currentMarketer?.marketerName || "",
    // Blank means "let the agent decide". In edit mode the stored flag is a
    // fact, so it is stated rather than guessed again.
    classification: (isEdit
      ? formData.isMedicine
        ? "MEDICINE"
        : "NON_MEDICINE"
      : formData.isMedicine
      ? "MEDICINE"
      : "") as ClassificationType | "",
    additionalContext: formData.description || "",
  });

  const [isFetching, setIsFetching] = useState(false);
  const [result, setResult] = useState<EnrichedProduct | null>(null);
  const [refs, setRefs] = useState<RefMap | null>(null);
  const [salts, setSalts] = useState<ResolvedSalt[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [isRuleSaved, setIsRuleSaved] = useState(false);

  const classificationType: ClassificationType = result
    ? result.flags.isMedicine
      ? "MEDICINE"
      : "NON_MEDICINE"
    : "MEDICINE";

  // -------------------------------------------------------------------------
  // Fetch
  // -------------------------------------------------------------------------

  const handleFetch = async () => {
    const productName = request.productName.trim();
    if (!productName) {
      toast.error("Product name is required");
      return;
    }

    setIsFetching(true);
    try {
      // Only what is actually filled in is sent; a blank field is left for the
      // agent to work out rather than sent as an empty string.
      const res = await ProductCatalogService.enrichProduct({
        productName,
        ...(request.brandName.trim()
          ? { brandName: request.brandName.trim() }
          : {}),
        ...(request.marketerName.trim()
          ? { marketerName: request.marketerName.trim() }
          : {}),
        ...(request.classification
          ? { classificationType: request.classification }
          : {}),
        ...(request.additionalContext.trim()
          ? { additionalContext: request.additionalContext.trim() }
          : {}),
      });

      if (!res?.success || !res.data) {
        toast.error(res?.message || "Could not fetch details from AI");
        return;
      }

      const data = res.data;
      setResult(data);
      setRefs({
        primaryCategory: data.primaryCategory,
        subCategory: data.subCategory,
        productGroup: data.productGroup,
        productFormGroup: data.productFormGroup,
        productForm: data.productForm,
        domain: data.domain,
        regulatoryDomain: data.regulatoryDomain,
        marketer: data.marketer,
        categoryHierarchy: data.categoryHierarchy,
      });
      setSalts(data.salts || []);
      setSelected({});
      setIsRuleSaved(false);
    } finally {
      setIsFetching(false);
    }
  };

  // -------------------------------------------------------------------------
  // Creating what is missing
  // -------------------------------------------------------------------------

  const createMaster = async (key: MasterKey) => {
    if (!refs) return;
    const ref = refs[key];
    const name = ref.name;
    if (!name) return;

    setBusyKey(key);
    try {
      let res: any;
      switch (key) {
        case "primaryCategory":
          res = await ProductCatalogService.createPrimaryCategory({
            name,
            classificationType,
          });
          break;
        case "subCategory":
          res = await ProductCatalogService.createSubcategory({
            name,
            classificationType,
          });
          break;
        case "productGroup":
          res = await ProductCatalogService.createGroup({
            name,
            classificationType,
          });
          break;
        case "productFormGroup":
          res = await ProductCatalogService.createFormGroup({ name });
          break;
        case "productForm":
          res = await ProductCatalogService.createForm({
            name,
            formGroupId: refs.productFormGroup.id as string,
          });
          break;
        case "domain":
          res = await ProductCatalogService.createDomain({ name });
          break;
        case "regulatoryDomain":
          res = await ProductCatalogService.createRegulatoryDomain({ name });
          break;
        case "marketer": {
          const payload = new FormData();
          payload.append("marketerName", name);
          res = await InverntroyService.addNewMarketer(payload);
          break;
        }
        case "categoryHierarchy":
          res = await ProductCatalogService.createHierarchy({
            classificationType,
            primaryCategoryId: refs.primaryCategory.id as string,
            subcategoryId: refs.subCategory.id as string,
            groupId: refs.productGroup.id as string,
          });
          break;
      }

      const created = res?.data;
      if (!res?.success || !created?.id) {
        toast.error(res?.message || `Could not add "${name}"`);
        return;
      }

      setRefs((prev) =>
        prev
          ? {
              ...prev,
              [key]: {
                id: created.id,
                name: created.name || name,
                isNew: false,
              },
            }
          : prev
      );
      toast.success(`"${created.name || name}" added`);
      if (key === "marketer") onMarketersChanged?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : `Could not add "${name}"`
      );
    } finally {
      setBusyKey(null);
    }
  };

  /** Points the reference at the existing master instead of adding a second spelling. */
  const useNearestExisting = (key: MasterKey) => {
    setRefs((prev) => {
      const near = prev?.[key]?.nearestExisting;
      if (!prev || !near) return prev;
      return {
        ...prev,
        [key]: { id: near.id, name: near.name, isNew: false },
      };
    });
  };

  const createSalt = async (index: number) => {
    const salt = salts[index];
    if (!salt?.name) return;
    setBusyKey(`salt.${index}`);
    try {
      const res = await ProductCatalogService.createSaltMaster({
        name: salt.name,
      });
      const created = res?.data;
      if (!res?.success || !created?.id) {
        toast.error(res?.message || `Could not add salt "${salt.name}"`);
        return;
      }
      setSalts((prev) =>
        prev.map((row, rowIndex) =>
          rowIndex === index
            ? {
                ...row,
                id: created.id,
                name: created.name || row.name,
                isNew: false,
              }
            : row
        )
      );
      toast.success(`Salt "${created.name || salt.name}" added`);
    } finally {
      setBusyKey(null);
    }
  };

  const useNearestSalt = (index: number) => {
    setSalts((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index || !row.nearestExisting) return row;
        return {
          ...row,
          id: row.nearestExisting.id,
          name: row.nearestExisting.name,
          isNew: false,
        };
      })
    );
  };

  /**
   * Records the sub-category to domain rule, so the next product in this
   * category fills its domain in by itself rather than coming back here.
   */
  const createDomainRule = async () => {
    if (!refs) return;
    setBusyKey("domainRule");
    try {
      const res = await ProductCatalogService.upsertSubcategoryDomainRule({
        primaryCategoryId: refs.primaryCategory.id as string,
        subcategoryId: refs.subCategory.id as string,
        domainId: refs.domain.id as string,
        regulatoryDomainId: refs.regulatoryDomain.id as string,
        isAllowed: true,
      });
      if (!res?.success) {
        toast.error(res?.message || "Could not save the domain rule");
        return;
      }
      setIsRuleSaved(true);
      toast.success("Domain rule saved");
    } finally {
      setBusyKey(null);
    }
  };

  // -------------------------------------------------------------------------
  // The review plan
  // -------------------------------------------------------------------------

  const masterSlots = useMemo(() => {
    if (!refs) return [];
    const slots: {
      key: MasterKey;
      label: string;
      ref: ResolvedRef;
      blockedReason?: string;
    }[] = [];

    const add = (key: MasterKey, label: string, blockedReason?: string) => {
      const ref = refs[key];
      if (!ref?.name) return;
      slots.push({ key, label, ref, blockedReason });
    };

    add("primaryCategory", "Primary Category");
    add("subCategory", "Sub Category");
    add("productGroup", "Product Group");
    add(
      "categoryHierarchy",
      "Category Mapping",
      refs.primaryCategory.id && refs.subCategory.id && refs.productGroup.id
        ? undefined
        : "Add the three categories above first"
    );
    add("productFormGroup", "Form Group");
    add(
      "productForm",
      "Product Form",
      refs.productFormGroup.id || !refs.productFormGroup.name
        ? undefined
        : "Add the form group first"
    );
    add("domain", "Domain");
    add("regulatoryDomain", "Regulatory Domain");
    add("marketer", "Marketer");
    return slots;
  }, [refs]);

  const plan = useMemo<PlanGroup[]>(() => {
    if (!result || !refs) return [];

    const groups: PlanGroup[] = [];
    const isMedicine = result.flags.isMedicine;

    /**
     * One row for one form field. Returns null when there is nothing to do:
     * no suggestion, or a suggestion the form already agrees with.
     */
    const row = (
      key: string,
      label: string,
      field: string,
      value: string | number | boolean | null,
      options: {
        /** The form pre-fills this field, so in create mode it counts as blank. */
        defaulted?: boolean;
        preview?: string;
        currentPreview?: string;
      } = {}
    ): PlanRow | null => {
      if (value === null || value === undefined || value === "") return null;
      const currentValue = formData[field];
      const currentFilled = !isBlank(currentValue);
      if (currentFilled && String(currentValue) === String(value)) return null;
      return {
        key,
        label,
        preview: options.preview ?? String(value),
        current: currentFilled
          ? options.currentPreview ?? String(currentValue)
          : "",
        patch: { [field]: value },
        overwrites: options.defaulted ? isEdit && currentFilled : currentFilled,
      };
    };

    const boolRow = (
      key: string,
      label: string,
      field: string,
      value: boolean,
      defaulted = true
    ) =>
      row(key, label, field, value, {
        defaulted,
        preview: value ? "Yes" : "No",
        currentPreview: formData[field] ? "Yes" : "No",
      });

    /**
     * A row whose value is a master id.
     *
     * When the master does not exist yet the row is still listed, blocked, so
     * the reviewer can see the field is waiting on something rather than
     * wondering why the agent said nothing about it.
     */
    const refRow = (
      key: string,
      label: string,
      field: string,
      ref: ResolvedRef,
      currentPreview?: string
    ): PlanRow | null => {
      if (!ref.name) return null;
      if (ref.id) {
        return row(key, label, field, ref.id, {
          preview: ref.name,
          currentPreview,
        });
      }
      return {
        key,
        label,
        preview: ref.name,
        current: isBlank(formData[field]) ? "" : currentPreview ?? String(formData[field]),
        patch: {},
        overwrites: false,
        blockedReason: `Add "${ref.name}" to the database first`,
      };
    };

    const push = (title: string, rows: (PlanRow | null)[], note?: string) => {
      const present = rows.filter((item): item is PlanRow => item !== null);
      if (present.length > 0) groups.push({ title, note, rows: present });
    };

    // ------------------------------------------------------------- identity
    push("Identity", [
      row("identity.name", "Product Name", "name", request.productName.trim()),
      row(
        "identity.brandName",
        "Brand Name",
        "brandName",
        request.brandName.trim()
      ),
      refRow(
        "identity.marketerId",
        "Marketer",
        "marketerId",
        refs.marketer,
        currentMarketer?.marketerName || formData.marketerId
      ),
    ]);

    // ------------------------------------------------------- classification
    push(
      "Classification & Flags",
      [
        boolRow("flags.isMedicine", "Is Medicine", "isMedicine", isMedicine),
        boolRow(
          "flags.isRxRequired",
          "Rx Required",
          "isRxRequired",
          result.flags.isRxRequired,
          false
        ),
        boolRow(
          "flags.isReturnable",
          "Returnable",
          "isReturnable",
          result.flags.isReturnable
        ),
        boolRow(
          "flags.coldChainRequired",
          "Cold Chain Required",
          "coldChainRequired",
          result.flags.coldChainRequired
        ),
        boolRow(
          "flags.discretePackaging",
          "Discrete Packaging",
          "discretePackaging",
          result.flags.discretePackaging
        ),
      ],
      "Turning Is Medicine on or off moves the product to the other category tree, which clears the mappings of the tree it leaves."
    );

    // ---------------------------------------------------- category & domain
    const hierarchy = refs.categoryHierarchy;
    const categoryRow: PlanRow | null =
      hierarchy.name && formData.categoryMappings.length === 0
        ? {
            key: "category.mapping",
            label: "Category Mapping",
            preview: hierarchy.name,
            current: "",
            patch: {
              categoryMappings: [
                {
                  hierarchyId: hierarchy.id as string,
                  primaryCategoryId: refs.primaryCategory.id as string,
                  primaryCategoryName: refs.primaryCategory.name,
                  subcategoryId: refs.subCategory.id as string,
                  subcategoryName: refs.subCategory.name,
                  groupId: refs.productGroup.id as string,
                  groupName: refs.productGroup.name,
                  isPrimary: true,
                },
              ] as CategoryMappingRow[],
            },
            overwrites: false,
            blockedReason: hierarchy.id
              ? undefined
              : "Add the category mapping above first",
          }
        : hierarchy.name
        ? {
            key: "category.mapping",
            label: "Category Mapping",
            preview: hierarchy.name,
            current: `${formData.categoryMappings.length} mapping(s) already set`,
            patch: {},
            overwrites: true,
            blockedReason:
              "This product is already mapped. Change it in Category & Domain.",
          }
        : null;

    push(
      "Category & Domain",
      [
        categoryRow,
        refRow("domain.domainId", "Domain", "domainId", refs.domain),
        refRow(
          "domain.regulatoryDomainId",
          "Regulatory Domain",
          "regulatoryDomainId",
          refs.regulatoryDomain
        ),
      ],
      "The domain is normally resolved from the category rules. Saving the rule above makes that happen by itself next time."
    );

    // ---------------------------------------------------------- composition
    if (isMedicine) {
      const unresolved = salts.filter((salt) => !salt.id);
      const basisUnit =
        canonical(result.packaging.baseUnit, STRENGTH_BASIS_UNITS) ?? "";
      const saltRow: PlanRow | null =
        salts.length > 0
          ? {
              key: "composition.salts",
              label: "Composition",
              preview: salts
                .map((salt) =>
                  salt.strengthValue
                    ? `${salt.name} ${salt.strengthValue}${salt.strengthUnit || ""}`
                    : salt.name || ""
                )
                .join(" + "),
              current:
                formData.salts.length > 0
                  ? `${formData.salts.length} salt row(s) already set`
                  : "",
              patch: {
                salts: salts.map(
                  (salt): SaltRow => ({
                    saltId: salt.id as string,
                    saltName: salt.name,
                    strengthValue:
                      salt.strengthValue != null
                        ? String(salt.strengthValue)
                        : "",
                    strengthUnit: salt.strengthUnit || "",
                    // The agent returns a strength but not what it is measured
                    // per, so the basis is seeded from the pack's base unit and
                    // left for the reviewer to confirm.
                    strengthBasisValue: "1",
                    strengthBasisUnit: basisUnit,
                    concentrationBasis: "",
                  })
                ),
              },
              overwrites: formData.salts.length > 0,
              blockedReason:
                unresolved.length > 0
                  ? `Add ${unresolved.length} new salt(s) below first - a part-resolved composition would match the wrong substitutes`
                  : undefined,
            }
          : null;

      push(
        "Composition",
        [
          saltRow,
          row(
            "composition.text",
            "Composition As Printed",
            "saltCompositionText",
            result.medicineAttributes?.saltCompositionText || null,
            { preview: truncate(result.medicineAttributes?.saltCompositionText || "") }
          ),
        ],
        `${result.compositionSource}: ${COMPOSITION_SOURCE_NOTES[result.compositionSource]}`
      );
    }

    // ------------------------------------------------------------ packaging
    push(
      "Packaging",
      [
        row(
          "pkg.packagingText",
          "Packaging Text",
          "packagingText",
          result.packaging.packagingText
        ),
        row("pkg.packUnit", "Pack Unit", "packUnit", result.packaging.packUnit),
        row(
          "pkg.packQuantity",
          "Pack Quantity",
          "packQuantity",
          result.packaging.packQuantity
        ),
        row("pkg.baseUnit", "Base Unit", "baseUnit", result.packaging.baseUnit),
        row(
          "pkg.multipackCount",
          "Multipack Count",
          "multipackCount",
          result.packaging.multipackCount,
          { defaulted: true }
        ),
      ],
      result.packaging.unmatchedValues.length > 0
        ? `Not applied because they are not valid values: ${result.packaging.unmatchedValues.join(
            ", "
          )}`
        : undefined
    );

    // ----------------------------------------------------------- attributes
    if (isMedicine) {
      push(
        "Medicine Attributes",
        [
          refRow(
            "attr.productForm",
            "Product Form",
            "medProductFormId",
            refs.productForm
          ),
          row("attr.route", "Route", "route", result.medicineAttributes?.route || null),
          row(
            "attr.releaseType",
            "Release Type",
            "releaseType",
            result.medicineAttributes?.releaseType || null
          ),
          row(
            "attr.disintegrationType",
            "Disintegration Type",
            "disintegrationType",
            result.medicineAttributes?.disintegrationType || null
          ),
        ],
        result.medicineAttributes?.unmatchedValues.length
          ? `Not applied because they are not valid values: ${result.medicineAttributes.unmatchedValues.join(
              ", "
            )}`
          : undefined
      );
    } else {
      push("Product Attributes", [
        refRow(
          "attr.productForm",
          "Product Form",
          "prodProductFormId",
          refs.productForm
        ),
        row("attr.variant", "Variant", "variant", result.productAttributes.variant),
        row("attr.flavour", "Flavour", "flavour", result.productAttributes.flavour),
        row(
          "attr.targetGroup",
          "Target Group",
          "targetGroup",
          canonical(result.productAttributes.targetGroup, TARGET_GROUPS)
        ),
        row(
          "attr.dietType",
          "Diet Type",
          "dietType",
          canonical(result.productAttributes.dietType, DIET_TYPES)
        ),
        row(
          "attr.sugarStatus",
          "Sugar Status",
          "sugarStatus",
          canonical(result.productAttributes.sugarStatus, SUGAR_STATUSES)
        ),
        row(
          "attr.keyIngredients",
          "Key Ingredients",
          "attrKeyIngredients",
          result.productAttributes.keyIngredients,
          { preview: truncate(result.productAttributes.keyIngredients || "") }
        ),
      ]);
    }

    // --------------------------------------------------------- product info
    const info = result.productInfo;
    if (info) {
      const infoFields: [string, string][] = [
        ["introduction", "Introduction"],
        ["primaryUse", "Primary Use"],
        ["howItWorks", "How It Works"],
        ["howToUse", "How To Use"],
        ["safetyAdvise", "Safety Advice"],
        ["ifMiss", "If A Dose Is Missed"],
        ["factBox", "Fact Box"],
        ["storage", "Storage"],
        ["commonSideEffect", "Common Side Effects"],
        ["interaction", "Interactions"],
        ["alcoholInteraction", "Alcohol Interaction"],
        ["pregnancyInteraction", "Pregnancy Interaction"],
        ["lactationInteraction", "Lactation Interaction"],
        ["drivingInteraction", "Driving Interaction"],
        ["kidneyInteraction", "Kidney Interaction"],
        ["liverInteraction", "Liver Interaction"],
        ["qAndA", "Q & A"],
        ["keyIngredients", "Key Ingredients"],
        ["keyBenefits", "Key Benefits"],
        ["productHighlights", "Product Highlights"],
        ["source", "Source"],
      ];
      push(
        "Product Info",
        infoFields.map(([field, label]) => {
          const value = (info as any)[field] as string | null;
          return row(`info.${field}`, label, field, value, {
            preview: truncate(value || ""),
            currentPreview: truncate(String(formData[field] || "")),
          });
        }),
        "Drafted by the model and stamped for review. These are medicine safety fields, so read them before publishing the product."
      );
    }

    return groups;
  }, [result, refs, salts, formData, isEdit, request, currentMarketer]);

  // -------------------------------------------------------------------------
  // Selection and apply
  // -------------------------------------------------------------------------

  const isRowSelected = (row: PlanRow) =>
    row.blockedReason ? false : selected[row.key] ?? !row.overwrites;

  const toggleRow = (row: PlanRow) =>
    setSelected((prev) => ({ ...prev, [row.key]: !isRowSelected(row) }));

  const setGroupSelection = (group: PlanGroup, value: boolean) =>
    setSelected((prev) => {
      const next = { ...prev };
      group.rows.forEach((row) => {
        if (!row.blockedReason) next[row.key] = value;
      });
      return next;
    });

  const selectedRows = plan.flatMap((group) =>
    group.rows.filter((row) => isRowSelected(row))
  );
  const blockedCount = plan.reduce(
    (total, group) =>
      total + group.rows.filter((row) => row.blockedReason).length,
    0
  );

  const handleApply = () => {
    if (selectedRows.length === 0) {
      toast.error("Nothing is selected to apply");
      return;
    }
    const patch: Record<string, any> = {};
    selectedRows.forEach((row) => Object.assign(patch, row.patch));
    onApply(patch);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const renderRefStatus = (ref: ResolvedRef) => {
    if (ref.id) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
          <Check className="w-3.5 h-3.5" />
          In the database
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
        <AlertTriangle className="w-3.5 h-3.5" />
        Not in the database yet
      </span>
    );
  };

  const canSaveDomainRule =
    !!refs?.primaryCategory.id &&
    !!refs?.subCategory.id &&
    !!refs?.domain.id &&
    !!refs?.regulatoryDomain.id;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-600" />
            <div>
              <h3 className="text-base font-semibold text-gray-800">
                Get Details From AI
              </h3>
              <p className="text-xs text-gray-500">
                Fills in only the fields that are still blank. Nothing is saved
                until you apply it and then save the product.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* ------------------------------------------------- the request */}
          <section className={formStyles.sectionCard}>
            <h4 className={`${formStyles.sectionTitle} mb-3`}>
              What we already know
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={formStyles.lableStyle}>
                  Product Name<span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  value={request.productName}
                  onChange={(e) =>
                    setRequest((prev) => ({
                      ...prev,
                      productName: e.target.value,
                    }))
                  }
                  className={formStyles.inputStyle}
                  placeholder="e.g. Dolo 650 Tablet"
                />
              </div>

              <div>
                <label className={formStyles.lableStyle}>
                  Medicine or Non-Medicine
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <select
                  value={request.classification}
                  onChange={(e) =>
                    setRequest((prev) => ({
                      ...prev,
                      classification: e.target.value as
                        | ClassificationType
                        | "",
                    }))
                  }
                  className={formStyles.inputStyle}
                >
                  <option value="">Let the AI decide</option>
                  <option value="MEDICINE">Medicine</option>
                  <option value="NON_MEDICINE">Non-Medicine</option>
                </select>
              </div>

              <div>
                <label className={formStyles.lableStyle}>Brand Name</label>
                <input
                  value={request.brandName}
                  onChange={(e) =>
                    setRequest((prev) => ({
                      ...prev,
                      brandName: e.target.value,
                    }))
                  }
                  className={formStyles.inputStyle}
                  placeholder="Leave blank if unknown"
                />
              </div>

              <div>
                <label className={formStyles.lableStyle}>Marketer</label>
                <input
                  list="enrich-marketer-options"
                  value={request.marketerName}
                  onChange={(e) =>
                    setRequest((prev) => ({
                      ...prev,
                      marketerName: e.target.value,
                    }))
                  }
                  className={formStyles.inputStyle}
                  placeholder="Leave blank if unknown"
                />
                <datalist id="enrich-marketer-options">
                  {marketerOptions.map((option) => (
                    <option key={option.id} value={option.marketerName} />
                  ))}
                </datalist>
              </div>

              <div className="md:col-span-2">
                <label className={formStyles.lableStyle}>
                  Anything Else That Helps
                </label>
                <textarea
                  value={request.additionalContext}
                  onChange={(e) =>
                    setRequest((prev) => ({
                      ...prev,
                      additionalContext: e.target.value,
                    }))
                  }
                  className={`${formStyles.inputStyle} h-20 resize-none`}
                  placeholder="Pack text, label text, a product URL - anything printed on the box"
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-gray-500">
                Only the fields filled in here are sent.
              </p>
              <button
                type="button"
                onClick={handleFetch}
                disabled={isFetching}
                className="flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm disabled:opacity-50"
              >
                {isFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Fetching, this takes a moment...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    {result ? "Fetch Again" : "Fetch Details"}
                  </>
                )}
              </button>
            </div>
          </section>

          {/* -------------------------------------------------- the result */}
          {result && refs && (
            <>
              {result.compositionSource === "MODEL_UNGROUNDED" && (
                <div className="flex items-start gap-2 border border-red-200 bg-red-50 rounded-xl px-4 py-3">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-700">
                    {COMPOSITION_SOURCE_NOTES.MODEL_UNGROUNDED}
                  </p>
                </div>
              )}

              {result.flags.isMedicine && salts.length === 0 && (
                <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 rounded-xl px-4 py-3">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-800">
                    This is a medicine but no composition came back. Enter the
                    salts by hand in Salt Details - a medicine without a
                    composition cannot be matched to substitutes.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="border border-gray-200 rounded-lg px-3 py-2">
                  <span className="text-gray-500">Classification</span>
                  <p className="font-medium text-gray-800 mt-0.5">
                    {result.flags.isMedicine ? "Medicine" : "Non-Medicine"}
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg px-3 py-2">
                  <span className="text-gray-500">Confidence</span>
                  <p className="font-medium text-gray-800 mt-0.5">
                    {result.confidence || "Not stated"}
                  </p>
                </div>
                <div className="border border-gray-200 rounded-lg px-3 py-2">
                  <span className="text-gray-500">Model</span>
                  <p className="font-medium text-gray-800 mt-0.5">
                    {result.model}
                  </p>
                </div>
              </div>

              {result.notes && (
                <p className="text-xs text-gray-600 border-l-2 border-gray-200 pl-3">
                  {result.notes}
                </p>
              )}

              {/* ------------------------------------------- the masters */}
              {masterSlots.length > 0 && (
                <section className={formStyles.sectionCard}>
                  <div className="mb-3">
                    <h4 className={formStyles.sectionTitle}>
                      Masters & Mappings
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      A reference has to exist in the database before the
                      product can point at it. Add whatever is missing here.
                    </p>
                  </div>

                  <div className="space-y-2">
                    {masterSlots.map((slot) => (
                      <div
                        key={slot.key}
                        className="flex items-start justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2.5"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-gray-500">
                              {slot.label}
                            </span>
                            {renderRefStatus(slot.ref)}
                          </div>
                          <p className="text-sm font-medium text-gray-800 mt-0.5 break-words">
                            {slot.ref.name}
                          </p>
                          {slot.ref.nearestExisting && !slot.ref.id && (
                            <p className="text-xs text-amber-700 mt-1">
                              "{slot.ref.nearestExisting.name}" already exists
                              and looks similar. Adding both would split the
                              taxonomy.
                            </p>
                          )}
                          {slot.blockedReason && !slot.ref.id && (
                            <p className="text-xs text-gray-500 mt-1">
                              {slot.blockedReason}
                            </p>
                          )}
                        </div>

                        {!slot.ref.id && (
                          <div className="flex items-center gap-2 shrink-0">
                            {slot.ref.nearestExisting && (
                              <button
                                type="button"
                                onClick={() => useNearestExisting(slot.key)}
                                className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                              >
                                Use existing
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => createMaster(slot.key)}
                              disabled={
                                !!slot.blockedReason || busyKey === slot.key
                              }
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                            >
                              {busyKey === slot.key ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Plus className="w-3.5 h-3.5" />
                              )}
                              Add to database
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    {salts.map((salt, index) => (
                      <div
                        key={`salt-${index}`}
                        className="flex items-start justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2.5"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-gray-500">Salt</span>
                            {renderRefStatus(salt)}
                          </div>
                          <p className="text-sm font-medium text-gray-800 mt-0.5 break-words">
                            {salt.name}
                            {salt.strengthValue
                              ? ` - ${salt.strengthValue}${
                                  salt.strengthUnit || ""
                                }`
                              : ""}
                          </p>
                          {salt.nearestExisting && !salt.id && (
                            <p className="text-xs text-amber-700 mt-1">
                              "{salt.nearestExisting.name}" already exists and
                              looks similar.
                            </p>
                          )}
                        </div>

                        {!salt.id && (
                          <div className="flex items-center gap-2 shrink-0">
                            {salt.nearestExisting && (
                              <button
                                type="button"
                                onClick={() => useNearestSalt(index)}
                                className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                              >
                                Use existing
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => createSalt(index)}
                              disabled={busyKey === `salt.${index}`}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                            >
                              {busyKey === `salt.${index}` ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Plus className="w-3.5 h-3.5" />
                              )}
                              Add to database
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* The rule is what makes the next product fill itself in. */}
                  <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      {isRuleSaved
                        ? "Domain rule saved. Products in this sub category will now resolve their domain by themselves."
                        : canSaveDomainRule
                        ? `Map ${refs.subCategory.name} to ${refs.domain.name} / ${refs.regulatoryDomain.name}, so this resolves by itself next time.`
                        : "A sub category to domain rule can be saved once the category, sub category, domain and regulatory domain all exist."}
                    </p>
                    <button
                      type="button"
                      onClick={createDomainRule}
                      disabled={
                        !canSaveDomainRule ||
                        isRuleSaved ||
                        busyKey === "domainRule"
                      }
                      className="shrink-0 px-3 py-1.5 text-xs font-medium text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-lg hover:bg-emerald-100 disabled:opacity-50"
                    >
                      {busyKey === "domainRule"
                        ? "Saving..."
                        : isRuleSaved
                        ? "Saved"
                        : "Save Domain Rule"}
                    </button>
                  </div>
                </section>
              )}

              {/* --------------------------------------------- the review */}
              {plan.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-lg">
                  Nothing to fill in: the form already holds everything the
                  agent returned.
                </p>
              ) : (
                plan.map((group) => (
                  <section key={group.title} className={formStyles.sectionCard}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <h4 className={formStyles.sectionTitle}>
                          {group.title}
                        </h4>
                        {group.note && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {group.note}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0 text-xs">
                        <button
                          type="button"
                          onClick={() => setGroupSelection(group, true)}
                          className="font-medium text-emerald-600 hover:text-emerald-800"
                        >
                          Select all
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                          type="button"
                          onClick={() => setGroupSelection(group, false)}
                          className="font-medium text-gray-500 hover:text-gray-700"
                        >
                          None
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {group.rows.map((row) => (
                        <label
                          key={row.key}
                          className={`flex items-start gap-3 border rounded-lg px-3 py-2.5 ${
                            row.blockedReason
                              ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                              : "border-gray-200 hover:border-emerald-200 cursor-pointer"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isRowSelected(row)}
                            disabled={!!row.blockedReason}
                            onChange={() => toggleRow(row)}
                            className="mt-1 accent-emerald-600"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-500">
                                {row.label}
                              </span>
                              {row.overwrites && !row.blockedReason && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700">
                                  Already filled in
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-800 mt-0.5 break-words">
                              {row.preview}
                            </p>
                            {row.current && (
                              <p className="text-xs text-gray-500 mt-0.5 break-words">
                                Currently: {row.current}
                              </p>
                            )}
                            {row.blockedReason && (
                              <p className="text-xs text-amber-700 mt-1">
                                {row.blockedReason}
                              </p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </section>
                ))
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t">
          <p className="text-xs text-gray-500">
            {result
              ? `${selectedRows.length} field(s) selected${
                  blockedCount > 0
                    ? `, ${blockedCount} waiting on a master`
                    : ""
                }`
              : "Nothing fetched yet"}
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!result || selectedRows.length === 0}
              className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              Apply To Form
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
